import React, { useState, useContext, useMemo, useRef } from "react";
import { Checkbox, FormControl, FormControlLabel, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "./OfferDetails.css";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import InfoIcon from '@mui/icons-material/Info';
import OfferManagementContext from "../../../context/offerManagement.context";
import { useEffect } from "react";
import { useAuth } from "../../../context/API_Client";
import { debounce, isEmpty } from "../../../pages/utils/utils";
import { customToast } from "../../customToaster/customToast";
import { useLocation } from "react-router-dom";
import LoadingLogoPage from "../../../pages/loadingPage/loadingPage";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FormHelperText from "@mui/material/FormHelperText";

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ],
};
const DEBOUNCE_TIME = 400;
export default function OfferDetails({ setIsNextButtonDisabled, setIsBtnLoading }) {
    const { postPspConfigForFiltering, getPaymentMethod, getAllEmiOffers } = useAuth();
    const {
        offerFieldData,
        setOfferFieldData,
        errorStateForOfferDetails,
        setErrorStateForOfferDetails,
        setEmiPlanTableData,
        setIssuerOptions,
        allPaymentMethods,
        setAllPaymentMethods,
        selectedEMIs,
        setEmiTypeOptions,
        setErrorMsz,
        currency,
        setCurrency,
        allIssuerRef,
        emiPlanTableData
    } = useContext(OfferManagementContext);
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const { type } = location.state?.editData || {};
    const today = new Date();
    const [selectedCurrency, setSelectedCurrency] = useState('INR'); //default currency is INR
    const htmlTagRegex = /.*<[^>]+>.*/;
    const fetchAllPaymentMethod = () => {
        getPaymentMethod(
            (data) => {
                const transformedData = data.filter((emi) => emi.brand !== "CardlessEMI");
                setAllPaymentMethods(transformedData);
            },
            (error) => {
                customToast("error", "high", error);
            }
        );
    }
    const fetchAllEmiOffer = () => {
        setIsBtnLoading(true);
        getAllEmiOffers(
            offerFieldData?.currencySelected, offerFieldData?.minAmount, offerFieldData?.maxAmount,
            (data) => {
                const transformedData = data?.filter((item) => {
                    item['offerType'] = 'NoCostEmi'; // set default to no cost EMI
                    if (!isEmpty(item.emiMethod)) {
                        item.emiMethod.merchantBorneInterestRate = item.emiMethod.interestRate;
                    }
                    item['isChecked'] = item.type === "Emi" && selectedEMIs.includes(Number(item.emiMethod.duration));
                    return (item.type === "Emi")
                });

                let issuerData = data
                    .filter((item) => item.type === "Emi" && (item?.brand === "CreditCardEMI" || item?.brand === "DebitCardEMI"))
                    .map((item) => ({
                        title: item.emiMethod.issuerTitle,
                        brand: item.emiMethod.issuer,
                        emiType: item.brand
                    }));
                issuerData = issuerData.filter((value, index, self) =>
                    index === self.findIndex((t) => (
                        t.title === value.title && t.brand === value.brand && t.emiType === value.emiType
                    ))
                );
                allIssuerRef.current = issuerData;
                if (isEmpty(issuerData)) {
                    setIsBtnLoading(false);
                    return setErrorMsz("No Issuer found for entered amount");
                }
                setIsBtnLoading(false);
                setErrorMsz("");
                setIssuerOptions(issuerData);
                setEmiPlanTableData(transformedData);
            },
            (error) => {
                customToast("error", "high", error);
                setIsBtnLoading(false);
            }
        );
    };
    useEffect(() => {
        fetchAllPaymentMethod();
    }, [])
    useEffect(() => {
        setErrorMsz('');
        if (isEmpty(emiPlanTableData)) fetchAllEmiOffer();
    }, [offerFieldData?.currencySelected, offerFieldData?.minAmount, offerFieldData?.maxAmount]);
    useEffect(() => {
        if (allPaymentMethods && allPaymentMethods?.length > 0) {
            const filteredData = allPaymentMethods?.filter(
                (item) => item?.type === "Emi"
            );
            const newOptions = filteredData?.map((item) => ({
                title: item?.title,
                brand: item?.brand,
            }));
            setEmiTypeOptions(newOptions);
        }
    }, [allPaymentMethods]);
    useEffect(() => {
        if (!isEmpty(currency)) return setIsLoading(false)
        postPspConfigForFiltering({},
            function (data) {
                setCurrency(data?.currencyCodes);
                setIsLoading(false);
            },
            function (errMsg) { }
        );
    }, [])
    const handleTitleChange = (e) => {
        const title = e.target.value;
        let errorText = htmlTagRegex.test(title) ? 'HTML tags are not allowed.' : (isEmpty(title) ? 'Required' : '');
        setErrorStateForOfferDetails(prevState => ({
            ...prevState,
            offerTitle: errorText,
        }));
        !htmlTagRegex.test(title) && setOfferFieldData((prev) => ({
            ...prev,
            offerTitle: title,
        }));
    };
    const handleStartDateChange = (newValue) => {
        setOfferFieldData((prev) => ({
            ...prev,
            offerStartDate: newValue,
        }));
    };
    const handleOfferStateDateError = (error) => {
        if (offerFieldData.startImmediately) return
        let errorText = !isEmpty(error) ? 'Selected Date/Time cannot be in the past' : '';
        setErrorStateForOfferDetails(prevState => ({
            ...prevState,
            offerStartDate: errorText,
        }));
    }
    const handleOfferEndDateError = (error) => {
        let errorText = !isEmpty(error) ? 'Selected Date/Time cannot be in the past' : '';
        setErrorStateForOfferDetails(prevState => ({
            ...prevState,
            offerEndDate: errorText,
        }));
    }
    useEffect(() => {
        const isError = !Object.entries(errorStateForOfferDetails).every(([key, value]) => {
            // check only if minAMount and maxAmount is present
            if ((key === 'minAmount' && isEmpty(value)) || (key === 'maxAmount' && isEmpty(value))) {
                return true
            }
            return (isEmpty(value) && !isEmpty(offerFieldData[key]))
        });
        setIsNextButtonDisabled(isError);
    }, [errorStateForOfferDetails, offerFieldData]);
    const handleEndDateChange = (newValue) => {
        setOfferFieldData((prev) => ({
            ...prev,
            offerEndDate: newValue,
        }));
    };
    const handleCurrencyChange = (event) => {
        setSelectedCurrency(event.target.value)
        setOfferFieldData((prev) => ({
            ...prev,
            currencySelected: event.target.value,
        }));
        setEmiPlanTableData([]);
    };
    const handleAmountError = (min, max, inputVar) => {
        let minA = !isEmpty(min) && Number(min);
        let maxA = !isEmpty(max) && Number(max);
        let errorText = inputVar === 'minAmount' ? "Min amount can't be greater than Max amount" : "Max amount can't be smaller than min amount"
        if (minA && maxA && minA > maxA) {
            setErrorStateForOfferDetails(prevState => ({
                ...prevState,
                [inputVar]: errorText,
            }));
        } else {
            setErrorStateForOfferDetails(prevState => ({
                ...prevState,
                minAmount: '',
                maxAmount: ''
            }));
        }
    };
    const handleMinAmount = (value) => {
        handleAmountError(value, offerFieldData?.maxAmount, 'minAmount');
        const numericValue = value.replace(/[^0-9]/g, '');
        setOfferFieldData((prev) => ({ ...prev, minAmount: numericValue }));
        setEmiPlanTableData([]);
    };
    const handleDebouncedMinAmountChange = debounce(handleMinAmount, DEBOUNCE_TIME);
    const handleMaxAmount = (value) => {
        handleAmountError(offerFieldData?.minAmount, value, 'maxAmount');
        const numericValue = value.replace(/[^0-9]/g, '');
        setOfferFieldData((prev) => ({ ...prev, maxAmount: numericValue }));
        setEmiPlanTableData([]);
    };
    const handleDebouncedMaxAmountChange = debounce(handleMaxAmount, DEBOUNCE_TIME);
    const handleRichTextChange = (value) => {
        if (isRichTextEmpty(value)) {
            setErrorStateForOfferDetails(prevState => ({ ...prevState, offerDescription: 'Required' }))
        } else {
            setErrorStateForOfferDetails(prevState => ({ ...prevState, offerDescription: '' }))
        }
        setOfferFieldData((prev) => ({ ...prev, offerDescription: value }));
    };
    useEffect(() => {
        if (selectedCurrency) {
            setOfferFieldData((prev) => ({ ...prev, currencySelected: selectedCurrency }));
        }
    }, [selectedCurrency]);
    function isRichTextEmpty(content) {
        const strippedContent = content.replace(/<[^>]*>?/gm, '').trim();
        return strippedContent === '';
    };
    const handleStartImmediatelyChange = (e) => {
        const currentDateTime = new Date(); // Get current date and time
        let isChecked = e.target.checked;
        let temp = { ...errorStateForOfferDetails };
        if (isChecked) {
            // if checked , set offerStartDate to curreant date time and remove offerStartDate from error data
            setOfferFieldData(prevState => ({ ...prevState, offerStartDate: currentDateTime })); // Set current date and time
            delete temp.offerStartDate;
            setErrorStateForOfferDetails(temp)
        } else {
            // if unchecked , set offerStartDate to null and add offerStartDate to error data
            setOfferFieldData((prev) => ({ ...prev, offerStartDate: null }));
            temp['offerStartDate'] = '';
            setErrorStateForOfferDetails(temp)
        }
        setOfferFieldData((prev) => ({ ...prev, startImmediately: e.target.checked }))
    };
    const isStartDateDisabled = type === 'edit' && offerFieldData?.offerStartDate < today;

    const ErrorTitle = ({ msz }) => {
        return <>
            <FormHelperText className={`error-msz-text ${!!msz ? "visible" : "hidden"}`}>
                <ErrorOutlineIcon fontSize="small" style={{ marginRight: "4px" }} />
                {msz}
            </FormHelperText>
        </>
    }

    return (
        <>
            {isLoading ? (
                <div className="content-wrapper whiteBg" style={{ marginLeft: 'auto' }}>
                    <section className="content offerManagementLoadingSection">
                        <LoadingLogoPage />
                    </section>
                </div>
            ) : (
                <div className="offerDetailsMainDiv">
                    <div>
                        <div div className="offerTitleTooltip offerDetailsFlexDiv" >
                            <Typography className="offerDetailsFieldTitle textfieldMarginBottom">Offer Title*</Typography>
                            <Tooltip title="Offer title is a name to identify the EMI offer for your reference.">
                                <IconButton
                                    sx={{ "&:hover": { backgroundColor: "transparent" } }}
                                    disableFocusRipple
                                    disableTouchRipple
                                ><InfoIcon />
                                </IconButton>
                            </Tooltip>
                        </div >
                        <TextField variant="outlined" id="outlined-basic" size="small" fullWidth
                            placeholder="eg: Season sale (This name won’t be displayed to the customer)"
                            value={offerFieldData?.offerTitle || ''}
                            onChange={handleTitleChange}
                        // error={!!errorStateForOfferDetails.offerTitle}  // Display error if there's a message
                        // helperText={errorStateForOfferDetails.offerTitle} // Show error message
                        />
                        <ErrorTitle msz={errorStateForOfferDetails.offerTitle} />
                    </div>
                    <div className="offerDetailsFieldWidth dateFieldDiv">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <div className="offerDateateField startDateField">
                                <Typography className="offerDetailsFieldTitle">Offer Start Date & Time*</Typography>
                                <DateTimePicker
                                    minDate={today}
                                    renderInput={(props) => (
                                        <TextField {...props}
                                            error={!!errorStateForOfferDetails.offerStartDate}  // Display error if there's a message
                                            helperText={errorStateForOfferDetails.offerStartDate ? "Please select a date" : ""} // Show error message
                                        />
                                    )}
                                    closeOnSelect={false}
                                    disablePast
                                    onError={() => !isStartDateDisabled ? handleOfferStateDateError : () => { }}
                                    disabled={offerFieldData?.startImmediately || isStartDateDisabled}
                                    timeSteps={{ hours: 1, minutes: 15, seconds: 0 }}
                                    onChange={handleStartDateChange}
                                    value={offerFieldData?.offerStartDate || null}
                                />
                                {
                                    errorStateForOfferDetails.offerStartDate && (
                                        <p className="error-msz-text">{errorStateForOfferDetails.offerStartDate}</p>
                                    )
                                }
                                <div className="checkboxDiv offerCheckbox offerCheckboxLabel offerCheckboxText">
                                    <FormControlLabel
                                        control={<Checkbox
                                            value="startImmediately"
                                            checked={offerFieldData?.startImmediately || false}
                                            onChange={handleStartImmediatelyChange}
                                            disabled={isStartDateDisabled}
                                        />}
                                        label="Start Immediately"
                                    />
                                </div>
                            </div>
                            <div className="offerDateateField endDateField">
                                <Typography className="offerDetailsFieldTitle">Offer End Date & Time*</Typography>
                                <DateTimePicker
                                    minDateTime={
                                        offerFieldData?.offerStartDate
                                            ? new Date(new Date(offerFieldData.offerStartDate).getTime()) // Start date + 1 ms
                                            : today
                                    }
                                    renderInput={(props) => (
                                        <TextField {...props}
                                            error={offerFieldData?.offerEndDate}
                                            helperText={offerFieldData?.offerEndDate ? "Please select a date" : ""}
                                        />
                                    )}

                                    closeOnSelect={false}
                                    disablePast
                                    onError={handleOfferEndDateError}
                                    timeSteps={{ hours: 1, minutes: 15, seconds: 0 }}
                                    onChange={handleEndDateChange}
                                    value={offerFieldData?.offerEndDate || null}
                                />
                                {
                                    errorStateForOfferDetails.offerEndDate && (
                                        <p className="error-msz-text">{errorStateForOfferDetails.offerEndDate}</p>
                                    )
                                }
                            </div>
                        </LocalizationProvider>
                    </div >

                    <div className="offerDetailsFieldWidth selectbackground dateFieldDiv currencyDropdownRadius">
                        <div className="startDateField">
                            <Typography className="offerDetailsFieldTitle textfieldMarginBottom">
                                Minimum Order Amount
                            </Typography>
                            <FormControl className="amountCurrencyField">
                                {currency?.length > 1 ?
                                    <Select
                                        size="small"
                                        className=""
                                        value={offerFieldData?.currencySelected ? offerFieldData?.currencySelected : selectedCurrency}
                                        onChange={handleCurrencyChange}
                                    >
                                        {currency?.map((cur) => (
                                            <MenuItem key={cur?.value} value={cur?.value}>
                                                {cur?.value}
                                            </MenuItem>
                                        ))}
                                    </Select> : <div className="currencyTextDiv">{currency[0]?.value}</div>}

                                <TextField variant="outlined" placeholder="00"
                                    id="outlined-basic" size="small" className="amountFieldRadius"
                                    defaultValue={offerFieldData?.minAmount || ''}
                                    value={offerFieldData?.minAmount || ''}
                                    onChange={(e) => handleMinAmount(e.target.value)}
                                    // onChange={(e) => handleDebouncedMinAmountChange(e.target.value)}
                                    error={!!errorStateForOfferDetails.minAmount}
                                />
                            </FormControl>
                            {
                                errorStateForOfferDetails.minAmount && (
                                    <p className="error-msz-text">{errorStateForOfferDetails.minAmount}</p>
                                )
                            }
                        </div>
                        <div className="endDateField">
                            <Typography className="offerDetailsFieldTitle textfieldMarginBottom">
                                Maximum Order Amount
                            </Typography>
                            <FormControl className="amountCurrencyField">
                                {currency?.length > 1 ?
                                    <Select
                                        value={offerFieldData?.currencySelected ? offerFieldData?.currencySelected : selectedCurrency}
                                        onChange={handleCurrencyChange}
                                        size="small"
                                    >
                                        {currency?.map((cur) => (
                                            <MenuItem key={cur?.value} value={cur?.value}>
                                                {cur?.value}
                                            </MenuItem>
                                        ))}
                                    </Select> : <div className="currencyTextDiv">{currency[0]?.value}</div>}
                                <TextField variant="outlined" placeholder="00" id="outlined-basic"
                                    size="small" className="amountFieldRadius"
                                    defaultValue={offerFieldData?.maxAmount || ''}
                                    value={offerFieldData?.maxAmount || ''}
                                    onChange={(e) => handleMaxAmount(e.target.value)}
                                    // onChange={(e) => handleDebouncedMaxAmountChange(e.target.value)}
                                    error={!!errorStateForOfferDetails.maxAmount}
                                />
                            </FormControl>
                            {
                                errorStateForOfferDetails.maxAmount && (
                                    <p className="error-msz-text">{errorStateForOfferDetails.maxAmount}</p>
                                )
                            }
                        </div>
                    </div>
                    <div className="termsAndConditionDiv">
                        <Typography className="offerDetailsFieldTitle">
                            Terms & Conditions*
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={offerFieldData?.offerDescription}
                            onChange={handleRichTextChange}
                            modules={modules}
                            placeholder="eg: Offer valid once per user during offer period"
                            className="richTextEditior"
                            style={{ borderColor: errorStateForOfferDetails.offerDescription ? 'rgb(211, 47, 47)' : null }}
                        />
                        <ErrorTitle msz={errorStateForOfferDetails.offerDescription} />
                        <div className="termsAndConditionDiv">
                            <Typography className="offerDetailsFieldTitle">
                                Note: Once you create the offer, you cannot change the start date & time
                            </Typography>
                        </div>
                    </div>
                </div >
            )
            }
        </>
    )
}